/**
 * GTA V meets TactSuit Giveaway 캠페인 — **단일 출처**.
 *
 * 팝업(홈)과 랜딩 페이지가 같은 값을 봐야 한다. 마감일·상품 수·폼 주소가 두 벌이 되면
 * 한쪽만 고쳐지고, 그게 「응모 마감일이 화면마다 다르다」로 나간다.
 *
 * ## 문구 — 화면 정본은 7로케일 `event.json`, `COPY` 는 영어 원문
 *
 * 초판(2026-09-09)은 「영어 한 벌, 번역 키 없음」이었다 — 시안·구글폼이 영어 전용이고 로케일
 * 확대가 미결이어서 문구를 이 파일 `COPY` 한 곳에 모았다. **2026-09-16(#5746) 에 7로케일로
 * 번역**해 `public/translations/<locale>/event.json` 이 생겼고, 화면은 `t()` 로 그쪽을 읽는다.
 * `COPY` 는 영어 원문으로 남아 페이지 메타(`popupDesc`)·테스트 등 일부 표면이 계속 쓴다.
 *
 * ⚠️ 새 문구는 **`event.json` 7로케일에 진짜 번역으로** 넣는다. 영문 값을 복사해 넣으면 가드가
 * 「번역 안 된 키」로 판정한다(`feedback-band-copy` 류). 2026-09-21 의 성공 카드 각주 두 벌
 * (`successFootnote`·`successFootnoteNoMail`)이 그 예다.
 *
 * ⚠️ 페이지는 `/ko/event/…` 등 다른 로케일에서도 열린다(Pages Router 기본) — 그 로케일의
 * `event.json` 이 나간다.
 */

/** 랜딩 경로. `trailingSlash: true` 라 링크는 슬래시까지 붙여 쓴다. */
export const GTA5_GIVEAWAY_PATH = "/event/gta5-giveaway/";

/**
 * 응모 폼 섹션의 앵커 (`gta5-giveaway.tsx` 의 `<section id="enter">`).
 *
 * ★**외부 링크는 이 주소를 준다** (작성자 확정 2026-09-09: *"외부 링크로 타고 왔을 때
 * 해당 폼 작성하는 부분으로 랜딩시켜 주실 수 있나요?"*).
 *
 * 인플루언서 QR·링크로 오는 사람은 이미 「무엇을 주는지」를 그 채널에서 보고 온다.
 * 페이지 맨 위로 떨어뜨리면 히어로와 3스텝을 다시 스크롤해야 하고, 그 구간이 그대로
 * 이탈 구간이다. 실측(390×844): 폼 위에 히어로 + 안내 460px 이 있고 폼 자체가 901px 다.
 *
 * ⚠️ 페이지를 **직접** 연 사람(홈 팝업 경유 포함)은 앵커 없이 위에서부터 읽는다 —
 * 응모하려면 YouTube 에 클립을 **먼저** 올려야 해서 폼이 시작이 아니라 마지막 단계다.
 * 그래서 페이지 기본 진입은 그대로 두고 **공유 링크에만** 이 값을 쓴다.
 *
 * 앵커 id 가 사라지면 브라우저는 조용히 맨 위로 보낸다(404 도 콘솔 경고도 없다) —
 * 그래서 가드가 이 상수와 페이지의 `id` 를 묶어서 검사한다.
 */
export const GTA5_GIVEAWAY_ENTER_ANCHOR = "enter";

/**
 * `How to Enter` 섹션 앵커 — 히어로의 두 번째 버튼이 여기로 내린다
 * (시안 `30559:16952` `Button` = "How to Enter").
 *
 * ⚠️ 앵커 id 가 사라지면 브라우저는 **조용히 맨 위로** 보낸다(404 도 콘솔 경고도 없다).
 * 그래서 가드가 이 상수와 페이지의 `id` 를 묶어서 검사한다 — `enter` 앵커와 같은 규약.
 */
export const GTA5_HOW_TO_ENTER_ANCHOR = "how-to-enter";

/** 외부 공유용 — 폼 섹션으로 바로 떨어진다. */
export const GTA5_GIVEAWAY_ENTER_PATH = `${GTA5_GIVEAWAY_PATH}#${GTA5_GIVEAWAY_ENTER_ANCHOR}`;

/**
 * `#enter` 착지 여백 — sticky 헤더 **바닥에서** 이만큼 띄운다.
 *
 * 머리말(`ENTER NOW` / `Submit Your Entry`)이 헤더에 딱 붙지 않게 하는 값이다.
 * 헤더 높이 자체는 `useStickyHeaderHeight` 가 실측하므로 여기엔 여백만 둔다.
 */
export const ENTER_LANDING_GAP = 24;

/**
 * `#enter` 재정렬을 허용하는 창(ms). 마운트 후 이 시간 안에 헤더 높이가 바뀌면 다시
 * 맞춘다 — 국가 선택 배너·레퍼럴·프로모 배너가 쿼리 뒤에 붙기 때문이다.
 *
 * 무제한으로 두면 **한참 뒤 배너가 바뀔 때 화면이 되돌아간다.** 사용자가 읽던 자리를
 * 뺏는 것이 정렬이 조금 어긋나는 것보다 나쁘다.
 */
export const ALIGN_WINDOW_MS = 2500;

/**
 * 사용자가 스크롤을 가져가는 신호. `scroll` 을 쓰면 안 된다 — **우리 스크롤도 그걸 쏜다.**
 */
export const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown"] as const;

/**
 * `#enter` 재정렬을 지금 해도 되나 — **판정을 효과 밖으로 뺀다.**
 *
 * 네 조건이 모두 참일 때만 맞춘다. 하나라도 빠뜨리면 그 자리가 곧 결함이다:
 *
 * ```
 *   hash        해시로 온 사람만 — 아니면 그냥 연 사람의 화면을 뺏는다
 *   headerHeight 훅이 값을 잡은 뒤 — 0 이면 여백이 24 뿐이라 헤더에 가린다
 *   stopped     사용자가 스크롤을 가져갔으면 포기
 *   now/until   마운트 후 창 안에서만 — 한참 뒤 배너 변화로 화면이 되돌아가면 안 된다
 * ```
 */
export function canRealignEnter({
  hash,
  headerHeight,
  stopped,
  now,
  until,
}: {
  hash: string;
  headerHeight: number;
  stopped: boolean;
  now: number;
  until: number;
}): boolean {
  if (hash !== `#${GTA5_GIVEAWAY_ENTER_ANCHOR}`) return false;
  if (headerHeight <= 0) return false;
  if (stopped) return false;
  return now <= until;
}

/**
 * ★**노출 시각** — 2026-09-12 18:00 **KST** (작성자 확정 2026-09-09, 재확정 09-10).
 *
 * KST 는 UTC+9 이고 **서머타임이 없다**. 그래서 18:00 KST = **09:00 UTC** 같은 날이다.
 * 오프셋을 문자열에 박아 두면(`+09:00`) 계산이 한 곳에 남고 리뷰에서 검산된다.
 *
 * ## ★★**시작만 KST 다 — 의도된 비대칭이다. 통일하지 말 것**
 *
 * 작성자 확정 2026-09-10: *"pdt 로 바꾸자 노출은 한국 오후 6시 부터야"* — **행사
 * 시각(마감·발표)은 PDT, 노출 시각만 KST** 다. 두 값의 성격이 다르기 때문이다:
 *
 * ```
 *   노출 9/12 18:00 KST   「우리가 언제 켜는가」 — 운영 시각. 화면에 안 나간다
 *   마감 10/18 23:59 PDT  「사용자에게 약속하는 마감」 — 화면·규정에 나간다
 *   발표 10/26 23:59 PDT  같음
 * ```
 *
 * 타임라인의 시작 칸은 날짜가 아니라 `Now` 라(시안 `30531:13735` `phase-Now`) 이
 * 비대칭이 **화면에 드러나지 않는다.** 그래서 어긋나 보이지 않는다.
 *
 * ⚠️ **「일관성」을 이유로 이 값을 PDT 로 바꾸면 노출이 16시간 밀린다**
 * (18:00 KST = 02:00 PDT → 18:00 PDT 는 한국 9/13 10:00). 아래 가드가 그 변경을 잡는다.
 */
export const GTA5_GIVEAWAY_OPEN_AT = new Date("2026-09-12T18:00:00+09:00");

/**
 * ★**마감 시각** — 2026-10-18 23:59:59.999 **PT**(= `2026-10-19T06:59:59.999Z`).
 *
 * 작성자 확정 2026-09-10. 날짜는 10/18 이고 **기준 시간대는 태평양시**다.
 *
 * ## ★왜 KST 가 아니라 PT 인가 — 이 리포의 관례이고, 방향이 안전하다
 *
 * 처음엔 KST 로 넣었다가 바꿨다. 종전 캠페인이 PT 를 쓴다:
 *
 * ```
 *   promo-banner.ts (종전 AI 프로모)   "미국 태평양시(PT) 7/27 자정"
 *   TactSuitPageBanner                "Holiday Sale. Nov 28 – Dec 15 (PST)"   ← 2026-10-01 삭제(고아)
 *   welcome-coupon.ts                 KST      ← 유일한 예외(국내 발급 마감)
 * ```
 *
 * 더 중요한 것은 **잘리는 방향**이다. 이 캠페인은 영어 한 벌 · YouTube 응모 ·
 * `shipped worldwide` 라 비한국 응모자가 주류인데, KST 마감이면:
 *
 * ```
 *   KST 23:59 마감   LA 10/18 07:59 에 잘린다   ← 배너가 "Ends Oct 18" 인 채로
 *   PT  23:59 마감   LA 10/18 23:59 · 서울 10/19 15:59
 * ```
 *
 * 두 안의 차이는 16시간이다. 미국 응모자가 **배너가 말하는 그날 아침에** 막히는 쪽을
 * 피했다.
 *
 * ⚠️ **오프셋을 `-07:00` 로 박았다 — 서머타임 때문이다.** 2026 미국 DST 는 11/1 에
 * 끝나므로 10/18 은 아직 **PDT(UTC-7)** 다(실측: 10/18 → `PDT` · 11/2 → `PST`).
 * **날짜를 11월 이후로 옮기면 `-08:00` 로 바꿔야 한다.** 아래 가드가 ISO instant 를
 * 대조하므로 오프셋만 고치고 날짜를 안 고치면(또는 반대면) 빨갛게 뜬다.
 *
 * ## ★표기가 다섯 곳이다 — 그래서 리터럴로 두지 않는다
 *
 * 화면과 규정이 마감을 다섯 곳에서 약속한다(`popupDesc` · `steps[0]` · `prizeLines` ·
 * `timeline` · 띠배너 `PROMO_BANNER.copy.ends`). Official Rules 가 그 기간을 실격
 * 근거로 쓴다 — *"valid view count **during the entry period**"*. 아래 라벨을 끼워
 * 넣으므로 **이 상수 한 줄**이면 다섯 곳이 따라온다.
 */
export const GTA5_GIVEAWAY_CLOSE_AT = new Date("2026-10-18T23:59:59.999-07:00");

/**
 * ★**마감일 표기는 상수에서 뽑는다** — 문구에 날짜를 **박지 않는다.**
 *
 * 종전엔 `"Ends Sept 30"` 처럼 다섯 곳에 리터럴로 박혀 있었다. 기간이 두 번 바뀌자
 * (9/30 → 10/18 → PT 기준) **상수와 문구를 따로** 고쳐야 했고, 하나라도 놓치면 화면이
 * 낡은 날짜를 약속한다. tsc·eslint 는 문자열 속 날짜를 못 본다.
 *
 * 파생시키면 그 클래스가 **사라진다** — 가드로 쫓는 것보다 낫다.
 *
 * ⚠️ **타임존을 명시한다**(`America/Los_Angeles`). 안 주면 브라우저의 로컬 존을 따라가
 * **동쪽 사용자에게 하루 늦게** 표기된다. 마감 instant 가 `2026-10-19T06:59:59.999Z`
 * 라 UTC+0 이상이면 이미 10/19 다 — 실측(Node 22 · 2026-09-10):
 *
 * ```
 *   America/Los_Angeles · America/New_York(02:59)   Oct 18
 *   UTC · Asia/Seoul · Pacific/Auckland             Oct 19   ← 밀린다
 * ```
 *
 * KST 안일 때보다 **밀리는 범위가 훨씬 넓다**(그때는 UTC+10 이상만). 서울 사용자에게
 * `Oct 19` 로 보이면 하루 긴 기간을 약속하는 셈이라 그냥 표시 오류가 아니다.
 *
 * ⚠️ 이 옵션을 지우면 이제 **KST 러너에서 빨갛게 뜬다**(`Oct 19`). 다만 CI 가 UTC 면
 * 역시 `Oct 19` 라 값으로도 잡힌다 — 그래도 소스 가드를 남긴다(러너 존에 의존하지
 * 않는 쪽이 낫다).
 *
 * 실측: `short` → `Oct 18` · `long` → `October 18`.
 */
const closeLabel = (month: "short" | "long"): string =>
  new Intl.DateTimeFormat("en-US", {
    month,
    day: "numeric",
    timeZone: "America/Los_Angeles",
  }).format(GTA5_GIVEAWAY_CLOSE_AT);

/** 짧은 표기 — 본문·배너용 (`Oct 18`). */
export const GTA5_CLOSE_LABEL = closeLabel("short");
/** 긴 표기 — 타임라인용 (`October 18`). */
export const GTA5_CLOSE_LABEL_LONG = closeLabel("long");

/**
 * ★**시간대를 문구에 적는다** (작성자 확정 2026-09-10).
 *
 * 날짜만 적으면 경계에 걸친 응모자가 *"내 시간으로 18일인데 왜 닫혔나"* 를 묻는다.
 * 종전 `TactSuitPageBanner` 가 이미 `Nov 28 – Dec 15 (PST)` 로 같은 일을 했다(그 컴포넌트는
 * 렌더 경로가 없는 고아라 2026-10-01 에 지웠다).
 *
 * ## ★`PT` 가 아니라 `PDT` 다 — 시안 실측
 *
 * 처음엔 계절 무관하게 `PT` 로 적었다(DST 경계에서 `PDT`/`PST` 가 틀리므로). 그런데
 * **시안이 `11:59 PM PDT` 로 못 박고 있다**(데스크탑 `30531:13735` 의 `phase-Oct 18`
 * ·`phase-Oct 26` · 2026-09-10 실측). 그리고 이 캠페인의 두 날짜는 **둘 다 DST 안**이라
 * `PDT` 가 참이다:
 *
 * ```
 *   2026 미국 DST 종료  11/1
 *   마감 10/18 · 발표 10/26   둘 다 그 전 → PDT (UTC-7)
 * ```
 *
 * ⚠️ **날짜를 11월 이후로 옮기면 이 라벨이 거짓이 된다** — 그때는 `PST` 이고 오프셋도
 * `-08:00` 이다. 가드가 ISO instant 와 라벨을 함께 잠가서 한쪽만 고치면 빨갛게 뜬다.
 */
export const GTA5_CLOSE_LABEL_TZ = `${GTA5_CLOSE_LABEL} (PDT)`;

/**
 * 공백 없는 판 — 시안 `30835:157633` 이 `Oct 18(PDT)` 로 **붙여서** 쓴다.
 *
 * ⚠️ 별 상수를 두는 이유: `GTA5_CLOSE_LABEL_TZ` 의 공백을 지우면 **기존 두 소비처**
 * (`popupDesc` · `errSubmitClosed`)의 문장이 같이 바뀐다. 그쪽은 산문이라 공백이
 * 맞다 — 시안이 다른 것은 **배너 한 줄**뿐이다.
 *
 * 시간대까지 한 곳에서 오게 하는 것이 요점이다. 이 캠페인은 10/18 마감이라 DST
 * 종료(11/1) 전이고 PDT 가 바뀔 일은 없지만, 리터럴로 두면 **날짜만 파생이고
 * 시간대는 아닌** 어중간한 상태가 된다.
 */
export const GTA5_CLOSE_LABEL_TZ_TIGHT = `${GTA5_CLOSE_LABEL}(PDT)`;
/** 긴 표기 + 시간대 — 타임라인용 (`October 18 (PDT)`). */
export const GTA5_CLOSE_LABEL_LONG_TZ = `${GTA5_CLOSE_LABEL_LONG} (PDT)`;

/**
 * ★**당첨 발표일** — 2026-10-26 (디자인 시안 2026-09-10, 작성자 전달).
 *
 * ⚠️ **마감에서 파생하지 않는다.** 「마감 + 8일」로 보이지만 그건 우연이고, 심사에
 * 걸리는 시간은 응모 수에 따라 운영이 정한다. 파생시키면 마감을 옮길 때 발표일이
 * **말없이 따라 움직인다** — 규정 문구라 그게 더 나쁘다.
 *
 * ⚠️ 마감(`Oct 18`)보다 **뒤여야 한다.** 종전 값이 `Oct 8` 이라 기간을 10/18 로
 * 옮긴 뒤 **발표가 마감보다 10일 빠른** 상태였다. 아래 가드가 순서를 잠근다.
 *
 * 날짜만 있고 시각이 없다 — 발표는 접수 마감처럼 초 단위로 갈리는 판정이 아니다.
 * 그래서 `Date` 가 아니라 표기 문자열이고, 순서 비교용 instant 만 따로 둔다.
 */
export const GTA5_ANNOUNCE_LABEL = "Oct 26";
/** 긴 표기 — 타임라인용. */
export const GTA5_ANNOUNCE_LABEL_LONG = "October 26";
/** 순서 가드용 instant (PT 자정 기준 — 마감과 같은 축에서 비교하기 위함). */
export const GTA5_ANNOUNCE_AT = new Date("2026-10-26T23:59:59.999-07:00");

/**
 * ★**시:분 표기** — 시안이 마감·발표 둘 다 `11:59 PM PDT` 로 보여준다
 * (`30531:13735` 의 `phase-Oct 18` · `phase-Oct 26` · 2026-09-10 실측).
 *
 * 종전엔 날짜만 있었다. 타임라인에서 날짜 아래 한 줄로 붙는다.
 */
export const GTA5_TIME_LABEL = "11:59 PM PDT";

/**
 * ★**상품 수 · 최소 영상 길이** — 문구 여러 곳이 같은 숫자를 말한다.
 *
 * 시안 `30536:234556`(`gv-prize-strip`)이 이 둘을 큰 숫자로 세운다(`10×` · `10s+`).
 * 그 띠와 본문 문구가 갈리면 **같은 화면에서 다른 숫자**를 약속한다 — 마감 표기가
 * 다섯 곳에서 어긋났던 것과 같은 클래스라 처음부터 상수로 둔다.
 *
 * 파생 자리(2026-09-10 실측):
 *
 * ```
 *   popupTitle      Win 1 of {N} TactSuit Pro
 *   prizeLines[0]   {N} TactSuit Pro, shipped worldwide — on us.
 *   prizeLines[1]   … Top {N} by view count …
 *   steps[0].desc   … {S} seconds or longer …
 *   gv-prize-strip  {N}× · {S}s+
 * ```
 *
 * ⚠️ **Official Rules 의 「30 days」는 다른 축이다** — 개인정보 보관 기간이라 여기서
 * 파생하지 않는다.
 */
/** 히어로 배경 — **글자 없는 판**(시안 `30559:16945` 추출 · 2880×800 = 1440×400 @2x). */
export const GTA5_GIVEAWAY_HERO_BG = "/images/event/gta5-giveaway-hero-bg.webp";

/**
 * ★**모바일 히어로는 다른 에셋이다** — 시안 `30743:234515` (재조회 2026-09-11).
 *
 * ⚠️ 종전 주석(`gta5-hero.tsx`)에 *"시안 모바일 에셋이 **2880×800** 으로 데스크탑
 * 배너와 같은 파일이다(2026-09-11 실측)"* 라고 적혀 있었다. **그날 아침엔 참이었고
 * 오후에 거짓이 됐다** — 디자이너가 세션 중에 바꿨다.
 *
 * ```
 *   데스크탑  2880×800  비율 3.600  (슬롯 1440×400)
 *   모바일     720×560  비율 1.286  (슬롯  360×280)
 * ```
 *
 * 비율이 **18:5 → 9:7** 로 바뀌면서 레이아웃 자체가 달라졌다(배경 한 장에 글자를
 * 얹는 구조 → 이미지가 위, 글자가 그 **밖**, 전폭 버튼 2개). 파일명이 같았고 배경
 * 이미지라 코드도 안 건드려서 **tsc·eslint·jest 3391건이 전부 초록**이었다.
 *
 * → 그래서 비율을 가드로 잠갔다(`__tests__/event/gta5-giveaway.test.tsx`
 * 「히어로 에셋 비율이 시안 슬롯과 같다」). 파일 존재·크기로는 안 잡힌다.
 */
export const GTA5_GIVEAWAY_HERO_BG_M =
  "/images/event/gta5-giveaway-hero-bg-m.webp";

/**
 * `Step Into the Game` 미디어 2장 — 시안 `30550:234175` 에서 추출(2026-09-10).
 *
 * 시안 슬롯은 **708×708 정사각**이다. 원본 해상도가 갈린다:
 *
 * ```
 *   gta5-ai-reads         1416×1416  = 708 @2x  ✔
 *   gta5-feel-instantly    708×708   = 708 @1x  ⚠️ 이게 Figma 원본의 최대다
 * ```
 *
 * ⚠️ 두 번째는 레티나에서 1x 라 살짝 무르게 보인다. `download_assets --pngScale 2` 로
 * 받아도 708 이 최대였다(원본 자체가 708). 더 큰 판이 오면 갈아 끼우면 되는
 * 자리다 — 경로만 바꾸면 된다.
 */
/**
 * 상품 카드 이미지 2장 — 시안 `30559:49033`(데스크탑) · `30743:234728`(모바일).
 *
 * ## ★**둘 다 시안 원본에서 다시 뽑았다** (2026-09-11 · 작성자 지적)
 *
 * 종전엔 **리포 공용 컷을 재사용**했다 — `pro-01` 은
 * `/images/devices/tactsuit-pro.png`(GNB·PDP·게임 라인업 5곳이 쓴다), `pro-02` 는
 * 먼저 뽑아 둔 `gta5-tactsuit-pro-open.webp`. 근거는 *"같은 컷이라 두 벌이 되면
 * 제품 사진이 갈린다"* 였고 **컷은 실제로 같다**(시안 에셋과 화면 대조 확인).
 *
 * 그런데 작성자가 *"기존이 좀 어두워 보였어"* 라고 했다 — 갈린 것은 **컷이 아니라
 * 압축**이다. 종전 `gta5-tactsuit-pro-open.webp` 가 **92KB** 라 검정 계조가
 * 뭉개져 있었다(새로 뽑은 것은 같은 1000² 에 140KB).
 *
 * ⚠️ **공용 에셋을 덮어쓰지 않는다.** `tactsuit-pro.png` 를 바꾸면 이 카드만이
 * 아니라 **5곳**이 같이 움직인다. 그래서 이 캠페인 전용 사본을 둔다 — 「두 벌이
 * 되면 갈린다」는 비용을 알고 받는 쪽이고, 그 대신 **여기 한 곳만** 바뀐다.
 *
 * 변환: 시안 2000² PNG → `sharp` webp `quality 92`, 1000².
 */
export const GTA5_PRIZE_CARD_IMAGES = [
  "/images/event/gta5-prize-pro-front.webp",
  "/images/event/gta5-prize-pro-open.webp",
] as const;

/** 상품 카드 CTA 목적지 — TactSuit Pro PDP. */
export const GTA5_PRIZE_CARD_HREF = "/shop/buy-tactsuit/tactsuit-pro";

export const GTA5_STEP_MEDIA = [
  {
    src: "/images/event/gta5-ai-reads.webp",
    title: "AI Reads the Action",
    desc: "TactSuit's AI listens to your game in real time — every gunshot, explosion, and footstep.",
  },
  {
    /**
     * ★**영상이다** — `/games/pc` 의 「Vibration Motors Bring It to Life」와 **같은
     * 클립**을 쓴다(작성자 지시 2026-09-11). 진동 모터가 실제로 도는 것을 보여 주는
     * 자리라 정지 이미지보다 이쪽이 문장(*"the instant it happens"*)과 맞는다.
     *
     * `PcAiBand.tsx:55` 가 쓰는 그 CDN 주소 그대로다 — 사본을 만들지 않는다.
     * 실측(2026-09-11): `HTTP 200 · video/mp4 · 2.26MB`.
     *
     * `src` 는 **폴백으로 남긴다.** 영상이 안 뜨는 환경(자동재생 차단·CDN 장애)에서
     * 빈 상자가 되지 않게, 그리고 `GTA5_STEP_MEDIA` 의 모양을 형제와 같게 유지한다.
     */
    src: "/images/event/gta5-feel-instantly.webp",
    video: "https://cdn.bhaptics.com/media/ath_responsive-haptics.mp4",
    title: "You Feel It Instantly",
    desc: "Vibration motors turn each moment into real feeling on your body, the instant it happens.",
  },
] as const;

/**
 * ★**해시태그·멘션** — 응모 성립 조건이라 한 글자만 틀려도 집계에서 빠진다.
 *
 * 6곳에 박혀 있었다(2026-09-10 실측 · `git grep '#GTA5TactSuit\|@bHaptics' -- src`).
 * 지금은 **네 자리**다 — 구 한페이지 폼(`Gta5EntryForm`)을 걷으면서 `COPY.checkHashtag`
 * / `checkMention` 이 소비처 0 이 돼 함께 지웠다:
 *
 * ```
 *   GTA5_CHECKBOX_VALUES.taggedHashtag / taggedMention   구글폼에 보내는 값 **이자 화면 라벨**
 *   COPY.steps[1].desc                                   설명문
 *   COPY.steps[1].chips                                  복사 칩 ← 사용자가 붙여 넣는 것
 * ```
 *
 * ★위저드는 `GTA5_CHECKBOX_VALUES.x` 를 **라벨로 그대로 쓴다**(`gta5-wizard-panes.tsx`).
 * 표시와 제출값이 **같은 상수**라 갈릴 수가 없다 — 종전엔 둘이 별개 문자열이라
 * 「일치하나」를 가드가 지켜야 했다.
 *
 * 마지막이 특히 위험하다 — **칩은 사용자가 그대로 복사해 YouTube 에 붙인다.** 칩만
 * 오타가 나면 사용자는 시키는 대로 했는데 응모가 집계에서 빠진다.
 *
 * ⚠️ 구글폼에 보내는 값과 화면 라벨은 이제 **같은 상수**다(위 참조) — 「우연히
 * 일치」가 아니라 구조적으로 하나다. 가드는 그 상수가 토큰을 품는지만 본다.
 */
export const GTA5_HASHTAG = "#GTA5TactSuit";
/** 태그할 계정. */
export const GTA5_MENTION = "@bHaptics";

/**
 * 성공 화면의 `Follow @bHaptics` 목적지 — 시안 `30581:7517`.
 *
 * 리포에 이미 같은 URL 이 있다(`contact-us-youtube.tsx`). 값을 여기서 새로 정하는
 * 것이 아니라 **그 값을 그대로** 쓴다 — 채널이 바뀌면 둘 다 본다.
 */
export const GTA5_BHAPTICS_YOUTUBE = "https://www.youtube.com/@bHaptics";

export const GTA5_PRIZE_COUNT = 10;
/** 응모 영상 최소 길이(초). */
export const GTA5_MIN_VIDEO_SECONDS = 10;

/**
 * ★**페이즈 프리뷰 — `?gta5-phase=closed|announced|open`** (작성자 요청
 * 2026-09-11: *"event/gta5-giveaway/ 에서 상태 볼 수 있게 쿼리해줘. 데브에서만
 * 가능하게"*).
 *
 * 마감 후·발표 후 화면은 **10/18 이 지나야** 자연히 보인다. 그때까지 검수할 방법이
 * 없으면 그 화면은 아무도 안 본 채로 릴리스된다 — 실측으로 그런 일이 이미 있었다
 * (리뷰 #5723 R1 M2: 페이지가 마감과 함께 404 라 `announced` 분기가 **prod 에서
 * 도달 불가**였고 그 자리가 빈칸인 것을 아무도 못 봤다).
 *
 * ## ★`now` 하나만 흔든다 — 컴포넌트에 분기를 넣지 않는다
 *
 * ```
 *   서버 now  →  isGta5PageVisible   페이지 노출
 *             →  canAcceptEntry      위저드 렌더 (entriesOpen)
 *             →  serverNow 프롭      카운트다운 페이즈 · 칩 · 누적 막대
 * ```
 *
 * 셋이 **이미 같은 `now` 를 읽는다.** 그래서 그 값만 갈면 화면 전체가 그 시점의
 * 모습이 된다 — 프리뷰 전용 분기가 코드에 하나도 안 생긴다. 분기를 심으면 그
 * 분기 자체가 검수되지 않는 새 표면이 된다.
 *
 * ## ⚠️ **prod·staging 에서는 동작하지 않는다**
 *
 * 한때 `?gta5-preview=1`·`?popup-demo=1` 이 prod 에서도 열려 있었다(staging 이
 * `NEXT_PUBLIC_ENV=production` 이라 비-prod 로 막으면 거기서 못 봐서). **그 둘은
 * 지웠고**(작성자 요청 2026-09-11 · 릴리스 후 쓸 일이 없다) 이것만 남는데, 이건
 * 처음부터 비-prod 전용이다 — 저 둘이 **보여주기만** 한 것과 달리 이건 화면이
 * 말하는 **시점 자체**를 바꾸기 때문이다:
 *
 * `?gta5-phase=open` 을 prod 에서 허용하면 마감 후에도 **응모 폼과 라이브 카운트다운이
 * 되살아난다.** 서버(`canAcceptEntry`)는 여전히 403 이라 제출이 안 되므로 「폼은 있는데
 * 안 됨」이 되고, 이 URL 은 **인플루언서 링크·QR 의 목적지**라 추측 가능한 쿼리가
 * 붙은 채로 공유될 수 있다. 캠페인 상태를 잘못 말하는 공개 주소가 된다.
 *
 * ⚠️ **근거를 한 번 틀렸다.** 첫 판은 *"`entriesOpen` 을 움직여 접수 창을 열 수
 * 있다"* 를 차단 근거로 적었는데, 그때(2026-09) `canAcceptEntry` 는 `if (!isProd) return true` 라
 * **dev 에선 원래 항상 열려 있었고**(#6149 에서 걷음) prod 에선 이 쿼리가 아예 안 먹는다 — 즉 「접수
 * 창이 열린다」는 어느 환경에서도 이 쿼리 때문에 일어나지 않는다. 차단은 여전히
 * 옳지만 이유가 위 문단이다.
 *
 * ⚠️ 게이트를 **이 함수 안에** 둔다. 호출처에서 `!isProd &&` 를 앞에 붙이는 형태면
 * 호출처가 늘 때 잊힌다 — 잊혀도 tsc·jest 가 안 잡는다.
 */
export const GTA5_PHASE_PARAM = "gta5-phase";

/**
 * ★**숫자로 받는다 — `?gta5-phase=1|2|3`** (작성자 요청 2026-09-11).
 *
 * 순서가 곧 타임라인이고, 화면의 타임라인 칸 순서와 같다. 이름으로 받던 때는
 * 카운트다운 쪽 이름(`judging`)과 이쪽 이름(`closed`)이 갈려 있었다.
 *
 * ⚠️ **1부터 센다**(작성자 요청) — 배열 인덱스가 아니라 **사람이 읽는 단계 번호**다.
 * 그래서 표를 `[v]` 로 인덱싱하지 않는다.
 *
 * ```
 *   1  개시 직후    응모 열림 · 라이브 카운트다운
 *   2  마감 직후    응모 닫힘 · 심사 중
 *   3  발표 직후    당첨자 발표
 * ```
 */
export const GTA5_PHASE_VALUES = ["1", "2", "3"] as const;

export type Gta5PhasePreview = (typeof GTA5_PHASE_VALUES)[number];

/**
 * 프리뷰 쿼리를 **시각**으로 바꾼다. 비-dev 이거나 값이 이상하면 `null` —
 * 호출처는 그때 실제 `now` 를 쓴다.
 *
 * 경계는 **상수에서 파생**한다(리터럴 날짜 금지). `closed` 는 마감 직후 1ms,
 * `announced` 는 발표 직후 1ms 로 잡아 **그 페이즈의 첫 순간**을 본다 — 가장
 * 어긋나기 쉬운 자리다.
 */
export function resolveGta5PhaseNow({
  isProd,
  value,
}: {
  isProd: boolean;
  value: string | string[] | undefined;
}): Date | null {
  if (isProd) return null;
  const v = Array.isArray(value) ? value[0] : value;
  /**
   * ⚠️ **인덱스로 표를 인덱싱하지 않는다** — `Number(v)` 는 `""`·`" "` 를 `0` 으로,
   * `"1abc"` 를 `NaN` 으로 만든다. 값 전수를 문자열로 대조해 **정확히 세 값만** 연다.
   */
  if (v === "1")
    return new Date(GTA5_GIVEAWAY_OPEN_AT.getTime() + 60 * 60 * 1000);
  if (v === "2") return new Date(GTA5_GIVEAWAY_CLOSE_AT.getTime() + 1);
  if (v === "3") return new Date(GTA5_ANNOUNCE_AT.getTime() + 1);
  return null;
}

/**
 * 캠페인을 지금 열어도 되나 — **팝업과 랜딩이 같은 답을 봐야 한다.**
 *
 * 둘이 갈리면 「팝업은 떴는데 눌러 보니 404」가 된다. 그래서 판정을 여기 한 곳에 두고
 * 두 표면이 **서버에서** 부른다(팝업도 홈 `getServerSideProps` 가 값을 내려 준다).
 *
 * ★**환경과 무관하게 시각으로만 판정한다** (#6149 · 2026-11-03). 종전엔 비-prod(local·dev)를
 * `if (!isProd) return true` 로 **항상 열림**으로 두었다(개발·검수 편의). 그래서 응모가
 * 10/18 에 끝난 뒤에도 dev·local 에서는 홈 팝업·GNB 띠·접수가 계속 살아 있었다
 * (할로윈 #6148 과 같은 클래스). 마감 이후 화면을 dev 에서 보려면 랜딩의
 * `?gta5-phase=1|2|3` 프리뷰를 쓴다 — 그 쿼리만 비-prod 전용으로 남는다.
 *
 * ⚠️ **`now` 를 인자로 받는다.** 안에서 `new Date()` 를 부르면 테스트가 시스템 시계에
 * 묶여 12일이 지나는 순간 「열림」 케이스와 「닫힘」 케이스 중 하나가 조용히 무효가 된다.
 */
/**
 * ★★**표면이 살아 있나** — 페이지 전용. **응모 창과 다른 질문이다.**
 *
 * ## 왜 갈라야 했나 (리뷰 #5723 R1 M2)
 *
 * 첫 판은 페이지 노출을 `isGta5GiveawayOpen`(= 응모 창)으로 판정했다. 그러면
 * **마감(10/18) 다음 날부터 페이지가 404** 다 — 응모자가 발표일(10/26)에 확인하러
 * 오면 사라져 있고, 규정·상품·발표일을 적어 둔 유일한 화면이 그 페이지다.
 *
 * ★그리고 그게 **시안 위반**이다. 디자이너 핸드오프(`30606:7324` ③, 2026-09-11 재조회)가
 * 마감 이후 상태를 **명시**한다:
 *
 * ```
 *   ENTRY_CLOSE ≤ now < WINNERS → ENTRIES CLOSE = active (마감·심사 중)
 *   now ≥ WINNERS               → WINNERS = active (발표 완료)
 * ```
 *
 * 두 상태 다 페이지가 렌더돼야 볼 수 있는데, 응모 창으로 막으면 prod 에서 **도달
 * 불가**다. `Gta5Countdown` 의 `judging`·`announced` 분기가 통째로 죽은 코드였다.
 *
 * ## 상한이 없다
 *
 * 발표일로 상한을 두면 `now ≥ WINNERS` 상태가 여전히 안 보인다 — 그 시각부터가
 * 그 상태이기 때문이다. 그래서 **시작 이후로는 계속** 연다.
 *
 * ⚠️ **응모 접수는 그대로 마감까지**다(`canAcceptEntry` → 창 밖이면 403). 페이지가
 * 열려 있다고 접수가 열린 게 아니다 — 그래서 마감 후에는 폼 대신 「마감」 화면을
 * 그린다(`gta5-giveaway.tsx` 의 `entriesOpen`).
 *
 * ⚠️ **팝업·띠배너는 여기를 쓰지 않는다.** 둘은 *"Enter Giveaway"* 로 유도하므로
 * **응모 창**(`isGta5GiveawayOpen`)을 따른다. 즉 `팝업 창 ⊂ 페이지 창` 이고, 이
 * 포함 관계가 종전 불변식(「팝업은 떴는데 눌러 보니 404」 금지)을 그대로 지킨다 —
 * 갈리면 안 되는 방향은 **페이지가 더 좁아지는 쪽**이었다.
 */
export function isAfterGiveawayOpen(now: Date): boolean {
  return now.getTime() >= GTA5_GIVEAWAY_OPEN_AT.getTime();
}

/** 랜딩 페이지 노출 판정 — 시작 이후 계속(위 docblock). */
export function isGta5PageVisible({ now }: { now: Date }): boolean {
  return isAfterGiveawayOpen(now);
}

/** 팝업·띠배너 노출 판정 — 응모 창(`isWithinEntryWindow`)과 같다. */
export function isGta5GiveawayOpen({ now }: { now: Date }): boolean {
  return isWithinEntryWindow(now);
}

/**
 * ★**응모 창 안인가** — 여는 시각 이상, 마감 시각 이하.
 *
 * 표면(페이지·팝업)과 **엔드포인트가 같은 판정을 본다.** 종전엔 이 판정이 표면에만
 * 있었고 `api/event/gta5-entry` 는 **한 번도 부르지 않았다**(리뷰 #5655 R1 n1) —
 * 그래서 세 구멍이 있었다:
 *
 * ```
 *   1  엔드포인트 직접 POST     시각·환경 무관하게 접수
 *   2  마감 이후에도 접수       닫는 조건이 없었다
 * ```
 */
export function isWithinEntryWindow(now: Date): boolean {
  const t = now.getTime();
  return (
    t >= GTA5_GIVEAWAY_OPEN_AT.getTime() &&
    t <= GTA5_GIVEAWAY_CLOSE_AT.getTime()
  );
}

/**
 * ★**접수해도 되나** — 엔드포인트 전용 판정.
 *
 * 표면(`isGta5GiveawayOpen`)과 **같은 창**을 본다 — 갈리면 「폼은 있는데 403」이다.
 *
 * 환경과 무관하다 — dev·local 도 창 밖이면 403 이다(#6149). 종전엔 dev 에서 제출을 검수하려고
 * 비-prod 를 항상 받았지만, 응모가 끝난 뒤에도 dev 에서 접수가 열려 있었다.
 */
export function canAcceptEntry({ now }: { now: Date }): boolean {
  return isWithinEntryWindow(now);
}

/**
 * 응모 폼 — **현재는 구글폼으로 내보낸다.**
 *
 * 기획 1순위는 「페이지 안에 신청서」였는데, 우리 서버가 성명·전화·주소를 받게 되면
 * 저장소·어드민 조회·처리방침 개정에 더해 Official Rules 가 이미 약속한
 * *"deleted 30 days after the event concludes"* 를 우리가 구현해야 한다. 발행일에
 * 맞추기 위해 이번에는 폼을 내보내고, 페이지 안 신청서는 **같은 URL 위에** 후속으로 얹는다.
 *
 * 실측(2026-09-09): 쿠키 없이 `viewform` 이 200 · 로그인 요구 문자열 0건 → 익명 응모 가능.
 * `https://forms.gle/4PeetnfoePBwRW77A` 는 같은 폼의 단축 별칭이다.
 */
export const GTA5_GIVEAWAY_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLScli5OSJboWXfNPFri-ffWlsntdapmJj8O4iBdy3MXrN_hZvA/viewform";

/**
 * 중계 대상. `viewform` 과 **같은 폼 id** 여야 한다 — 아래 가드가 대조한다.
 *
 * ★**`?hl=en` 으로 응답 언어를 영어로 고정한다** (#5811 · 2026-10-14). 서버 요청에는 언어 헤더가
 * 없어서 구글이 **요청 IP 위치로** 응답 언어를 정한다 — 확인 페이지가 유럽(`fra1`)에서는 독일어 등으로
 * 와서, 영어·한국어 확인 문구만 보는 판정(`isConfirmationPage`)이 **접수된 응모를 실패로** 돌렸다
 * (고객 응모가 시트에 여러 번 들어가 있는데 화면은 실패 — 재시도가 중복을 만들었다).
 *
 * 실측(2026-10-14 · 한국 IP · `viewform`·`formResponse` GET, 제출 없음):
 *
 * ```
 *   헤더 없음                         lang="ko"   ← IP 기반
 *   Accept-Language: en / en-US       lang="ko"   ← 영어 헤더는 무시된다
 *   Accept-Language: de-DE            lang="de"
 *   ?hl=en                            lang="en"   ← 이것만 영어로 고정된다
 * ```
 *
 * ⚠️ `?hl=en` 과 함께 **비영어 `Accept-Language` 를 보내면 그쪽이 이긴다**(`?hl=en` + `de-DE` → de).
 * 중계는 언어 헤더를 보내지 않는다 — 헤더를 추가하는 변경은 이 고정을 깬다.
 */
export const GTA5_GIVEAWAY_FORM_POST_URL = `${GTA5_GIVEAWAY_FORM_URL.replace(
  /\/viewform.*$/,
  "/formResponse",
)}?hl=en`;

/**
 * 응모 확인 메일 — 제목.
 *
 * ★**요청 원문 그대로**(마케팅 요청 2026-09-21: *"같은 내용을 여러번 접수하는 사람들이
 * 있어서 … 'Your GTA V meets TactSuit Giveaway Entry is Confirmed!' 라고 자동으로 메일이
 * 가면"*). 캠페인명 `meets` 는 `COPY.heroTitle` 과 같은 공식 표기다.
 *
 * ## 왜 우리가 보내나 — 구글폼 기본 기능은 못 쓴다
 *
 * 구글폼의 「응답 사본 보내기」는 ① 제목을 못 바꾸고 ② 「이메일 주소 수집」을 켜야 한다.
 * 켜면 `formResponse` 에 필수 `emailAddress` 파라미터가 생기는데, 중계는 그 값을 **항상
 * 동봉**한다(`GTA5_GOOGLE_EMAIL_PARAM`) — 운영이 켜도 접수가 유지되도록 했다. ⚠️ **켜진 상태
 * 실측은 미확인**(추정 — 검증 경계는 그 상수 주석). 실측(2026-09-21 · viewform): 지금은 꺼져
 * 있다(`emailAddress` 0건).
 *
 * ⚠️ 켜면 **구글 사본 메일과 우리 확인 메일이 둘 다** 간다 — 하나를 고른다(마케팅 결정).
 * 「인증된 이메일(Verified)」 모드는 구글 로그인이 필요해 서버 중계로는 **불가** — 켜려면
 * 「응답자 입력(Responder input)」 모드여야 한다.
 *
 * ## 본문은 고정 · 사용자 입력 0
 *
 * 비인증 엔드포인트가 「응모자가 적은 주소」로 메일을 보내게 된다. 성명·영상 링크를
 * 본문에 실으면 공격자가 `no-reply@bhaptics.com` 발신으로 임의 문구·링크를 아무에게나
 * 보낼 수 있다. 그래서 템플릿(`emails/Gta5EntryConfirmationEmail`)은 **props 가 없다** —
 * 실을 자리 자체가 없게 했다.
 */
export const GTA5_CONFIRMATION_SUBJECT =
  "Your GTA V meets TactSuit Giveaway Entry is Confirmed!";

/**
 * 구글폼 「이메일 주소 수집」이 켜졌을 때 `formResponse` 가 **필수로** 요구하는 파라미터 이름.
 *
 * 중계는 이 값을 **항상** 보낸다(응모자가 적은 이메일). 켜져 있으면 필수를 채우고, 꺼져
 * 있으면 구글이 모르는 파라미터로 버린다 — `entry.*` 와 같은 태도.
 *
 * ★**검증 경계** (2026-09-22):
 *
 * ```
 *   꺼진 상태(지금)   실측 ✓  `toGoogleForm` 과 같은 전체 payload(9필드 + fvv/pageHistory)에
 *                            이 값을 동봉해 라이브 폼에 직접 POST 1건 → HTTP 200 · 73,689 bytes ·
 *                            확증 표식 「응답이 기록되었습니다」 1건 · "This is a required question" 0건
 *   켜진 상태         미확인  구글이 `emailAddress` 를 필수로 요구하고 이 값으로 통과된다는 것은
 *                            **추정**이다. 운영 폼을 임의로 켜 볼 수 없으므로 폼 소유자가 **사본 폼**을
 *                            만들어 켠 뒤 그쪽에 POST 해 보는 것이 검증 경로 — follow-up.
 * ```
 *
 * 종전엔 보내지 않아 "운영이 그 설정을 켜면 안 된다" 는 안내가 따라다녔다. 실제로 메일을 보내는
 * 기능 옆에서 "이메일 수집을 켜지 말라" 는 요청은 이해되지 않는다(작성자 지적 2026-09-22) —
 * 동봉으로 그 제약을 없앴다.
 *
 * ⚠️ 「인증된 이메일(Verified)」 모드는 구글 로그인 세션이 필요해 이 파라미터로도 못 넘는다.
 * 서버 중계가 가능한 것은 「응답자 입력(Responder input)」 모드다. `npm run gta5:check-form` 이
 * 수집 상태를 정보용으로 찍는다.
 */
export const GTA5_GOOGLE_EMAIL_PARAM = "emailAddress";

/**
 * 문의처 — 메일 **본문**에 적는다.
 *
 * ⚠️ `sendEmail()` 의 `replyTo` 는 login 의 `CustomEmail` 에 필드가 없어 **조용히 버려진다**
 * (Spring 이 미지 프로퍼티를 무시한다). Reply-To 헤더에 의존하지 말 것.
 */
export const GTA5_SUPPORT_EMAIL = "support@bhaptics.com";

/**
 * 이메일 형식 — **위저드와 라우트가 같은 술어를 쓴다.**
 *
 * 종전엔 위저드(`gta5-wizard-panes`)에만 이 정규식이 있었고 라우트는 「비어 있지 않은가」만
 * 봤다. 메일을 보내게 되면서 라우트도 형식을 보는데, 두 벌이면 클라를 통과한 값이 서버에서
 * 거절되는(또는 반대) 틈이 생긴다. `CommonUtils.isEmail`(validator) 은 이보다 엄격해서
 * 쓰지 않는다 — 위저드가 받아들인 주소를 서버가 거절하면 사용자는 이유 없는 실패만 본다.
 *
 * ⚠️ **주소 구분자를 배제한다** — `,` `;` `<` `>` 큰따옴표·괄호·역슬래시 (strict review R1,
 * 2026-09-21). 이 값은 그대로 login → SendGrid 의 수신자 필드로 간다. 수신자 리스트 파서
 * (JavaMail `InternetAddress.parse` 류)가 콤마를 구분자로 읽으면 `x,victim@real.com` 한 값이
 * **둘째 수신자**가 된다 — login 쪽 파싱을 이 레포에서 확인할 수 없으니 여기서 막는다.
 * 인용 로컬파트(`"a,b"@x.com`)는 RFC 상 유효하지만 실사용이 없어 거절을 감수한다.
 *
 * ⚠️ **아포스트로피(`'`)는 배제하지 않는다** (R2 정정). 어떤 수신자 파서도 구분자로 안 쓰고,
 * `o'connor@…` 류는 실재하는 정상 주소다 — 위협 모델과 무관한 문자를 빼면 응모자만 잃는다.
 */
export const GTA5_EMAIL_PATTERN =
  /^[^\s@,;<>"()[\]\\]+@[^\s@,;<>"()[\]\\]+\.[^\s@,;<>"()[\]\\]+$/;

/**
 * 구글폼 필드 id — **폼을 수정하면 깨진다.**
 *
 * 실측(2026-09-09 · `viewform` 의 `FB_PUBLIC_LOAD_DATA_` 파싱). 상품·일정이 「미정」
 * 이라 폼이 수정될 가능성이 있고, id 가 바뀌면 **응모가 조용히 유실된다** — 구글은
 * 모르는 `entry.*` 를 오류 없이 버린다.
 *
 * → `npm run gta5:check-form` 이 라이브 폼을 받아 이 9개가 그대로인지 대조한다.
 *   발행 전과 캠페인 중에 돌린다.
 */
export const GTA5_ENTRY_IDS = {
  tagged: "entry.611588035",
  videoLink: "entry.577327358",
  fullName: "entry.1639998784",
  phone: "entry.357778781",
  email: "entry.1540548320",
  address: "entry.683739177",
  postalCode: "entry.104781674",
  marketing: "entry.471913387",
  rules: "entry.379811619",
} as const;

/**
 * 체크박스 보기 문자열 — **한 글자라도 다르면 구글이 조용히 버린다.**
 * 위 실측에서 원문 그대로 옮겼다. 번역·다듬기 금지.
 */
export const GTA5_CHECKBOX_VALUES = {
  taggedHashtag: `I tagged ${GTA5_HASHTAG} in my video`,
  taggedMention: `I tagged ${GTA5_MENTION} in my video`,
  /**
   * ★**2026-09-10 폼 실측에서 새로 나온 보기.** 기획자가 추가했다.
   *
   * 「선정되면 후속 영상에서 TactSuit Pro 를 착용하겠다」는 동의다 — 시안의
   * `How to Enter` 4번째 스텝(`Step 4 — For Winners: Play It With TactSuit`)과
   * 짝이다. 같은 체크박스 그룹(`entry.611588035`)에 들어간다.
   */
  followUpVideo:
    "If selected, I agree to wear the TactSuit Pro for the follow-up video.",
  marketing:
    "I consent to bHaptics using my information for marketing purposes. (Optional)",
  ruleAge: "I confirm that I am 18 years of age or older. (Required)",
  ruleAgree: "I have read and agree to the Official Rules above. (Required)",
  /**
   * ★**필수 동의가 셋이 됐다** — 시안 Step 3 의 세 번째 항목(`30597:7336`)이 이것이다.
   *
   * ⚠️ **`(Required)` 접미사까지 정확히** 보내야 한다. 구글은 모르는 보기를 조용히
   * 버리므로, 화면 라벨에서 그 접미사를 빼도 **전송 값은 이 문자열 그대로**여야 한다.
   */
  ruleVideoMarketing:
    "I consent to bHaptics using my TactSuit gameplay video for marketing purposes. (Required)",
} as const;

/**
 * 히어로 배너 — **임시 자산이다.**
 *
 * Figma `C6hxMBJmonrz0ryqHPfMp7` 노드 `2333:1175`(2171×727)를 그대로 받은 것이라
 * **문구가 이미지에 구워져 있다.** 그래서 지금은 그 위에 라이브 텍스트를 얹지 않는다
 * (겹친다).
 *
 * ⚠️ 최종본은 **글자 없는 배경판**이 필요하다 — 조립본 시안(`2312:615`)의 히어로에는
 * `Enter Giveaway`·`How to Enter` 버튼이 라이브로 얹혀 있다. 글자를 구워 두면 번역도
 * 스크린리더도 반응형도 안 된다.
 */
/**
 * ★**팝업 배너 배경 — 글자 없는 판** (시안 `30835:157629`, 2026-09-11).
 *
 * 종전 `GTA5_GIVEAWAY_HERO` 는 **문구가 이미지에 구워진 임시 자산**이라 그 위에
 * 라이브 텍스트를 얹을 수 없었다(그 자리 주석: *"최종 배경판이 오면 제목·부제를
 * DOM 으로 올린다"*). 이제 왔다 — 왼쪽이 어둡게 처리돼 흰 글자가 읽힌다.
 *
 * ```
 *   720×380   비율 1.895 = 슬롯 360×190 의 정확히 2배
 * ```
 *
 * 비율은 가드로 잠근다 — 히어로에서 파일명이 같은 채 18:5 → 9:7 로 바뀌어
 * tsc·jest 3391건이 전부 초록이었던 전례가 있다.
 */
export const GTA5_GIVEAWAY_POPUP_BG =
  "/images/event/gta5-giveaway-popup-bg.webp";

export const GTA5_GIVEAWAY_HERO = "/images/event/gta5-giveaway-hero.jpg";

/** 히어로 원본 비율 — 팝업·페이지가 같은 판을 쓰므로 한 곳에서 준다. */

/** 응모 폼이 쓰는 값. 라우트(`api/event/gta5-entry`)와 폼이 같은 모양을 본다. */
export type Gta5EntryFields = {
  videoLink: string;
  fullName: string;
  phone: string;
  email: string;
  address: string;
  postalCode: string;
  taggedHashtag: boolean;
  taggedMention: boolean;
  followUpVideo: boolean;
  ruleAge: boolean;
  ruleAgree: boolean;
  ruleVideoMarketing: boolean;
  marketing?: boolean;
};

/**
 * 화면 문구. 영어 한 벌 — 위 docblock 참조.
 *
 * ★`steps[2]` 는 **시안 원문 그대로**다(*"Drop your video link below"*). 폼이 페이지
 * 안에 있어야 참인 문장이고, 지금 그렇다. 응모를 다시 외부로 내보내면 이 문장이
 * 거짓이 되므로 가드가 **폼 렌더 여부와 묶어** 검사한다.
 */
export const COPY = {
  /**
   * ★**히어로 — 라이브 텍스트다** (시안 `30559:16945` · 2026-09-10 실측).
   *
   * 종전엔 문구가 배경 이미지에 **구워져** 있었다. 그러면 번역·스크린리더·반응형이
   * 전부 안 되고, 마감일이 바뀌면 이미지를 다시 받아야 한다(실제로 9/30 → 10/18 로
   * 두 번 바뀌었다).
   *
   * 시안이 글자 없는 배경판을 주므로 얹는다. `heroHighlight` 는 노란 줄이고 **마감일이
   * 그 안에 있어** 상수 파생이다.
   */
  heroTitle: "GTA V meets TactSuit",
  heroSub: "Los Santos hits different when you can feel it.",
  heroHighlight: `Win 1 of ${GTA5_PRIZE_COUNT} TactSuit Pro — ends ${GTA5_CLOSE_LABEL}`,
  heroCtaEnter: "Enter Giveaway",
  heroCtaHow: "How to Enter",

  /**
   * ★**팝업 배너 문구** — 시안 `30835:157631` (2026-09-11).
   *
   * ```
   *   eyebrow  GTA V meets TactSuit                              12/16 .4
   *   title    Post Your GTA V PC Play Clip by Oct 18(PDT).  14/18 SemiBold .25
   *   각주      *No TactSuit required to enter                10/14 .4
   * ```
   *
   * ⚠️ 위 표는 **시안 인용이 아니라 우리가 렌더하는 값**이다. 시안과 두 군데 다르다:
   *
   * ```
   *   시안 원문        GTA V x Tasct Suit
   *   ①  Tasct Suit → TactSuit    조직 규칙상 제품명은 정확히 쓴다 (시안 오타)
   *   ②  x → meets                `x` 가 **공식 콜라보로 읽힌다**는 지적 (2026-09-16)
   * ```
   *
   * ②는 표기 정정이 아니라 **의미 판단**이다 — `x` 는 브랜드 협업 표기(Nike x …)의
   * 관용이라 Rockstar 와의 공식 제휴로 오해될 수 있다. `meets` 는 그 함의가 없다.
   * 시안이 갱신되면 ②가 흡수될 수 있으니 **시안을 다시 뜰 때 이 줄을 먼저 본다.**
   *
   * ⚠️ 마감일은 **리터럴로 박지 않는다** — `GTA5_CLOSE_LABEL` 파생이다. 시안엔
   * `Oct 18(PDT)` 로 적혀 있지만 기간이 바뀌면 다섯 곳이 갈린다(#5655 클래스).
   */
  popupEyebrow: "GTA V meets TactSuit",
  popupBanner: `Post Your GTA V PC Play Clip by ${GTA5_CLOSE_LABEL_TZ_TIGHT}.`,
  popupFootnote: "*No TactSuit required to enter",

  popupTitle: `Win 1 of ${GTA5_PRIZE_COUNT} TactSuit Pro`,
  popupDesc: `Post your GTA V clip on YouTube. Entries close ${GTA5_CLOSE_LABEL_TZ}.`,
  cta: "Enter Giveaway",

  howToEnterEyebrow: "Event overview",
  howToEnterTitle: "How to Enter",
  howToEnterDesc: `The ${GTA5_PRIZE_COUNT} videos with the highest valid view counts will each win a TactSuit Pro. Winners are selected by view counts, not by random draw — notified individually by email and announced on bHaptics' official social media channels.`,
  /**
   * ★**시안 `30537:6852` 그대로** (2026-09-10 실측). 종전엔 제목이
   * `Step 1 — Play & Film` 이었는데 시안은 번호를 **배지로** 빼고 제목은 한 단어다
   * (`Film` · `Upload & tag` · `Submit`). 번호를 글자에 남기면 배지와 두 벌이 된다.
   *
   * `chips` 는 **데이터로** 둔다 — 「2번 카드에만」을 인덱스로 박으면 순서가 바뀔 때
   * 엉뚱한 카드에 붙는다. 값은 `GTA5_HASHTAG`·`GTA5_MENTION` 파생이다 —
   * 체크박스 문장은 그 토큰을 **품고 있을 뿐** 같은 문자열이 아니다.
   */
  steps: [
    {
      title: "Film",
      desc: `Film yourself playing GTA5 on PC (${GTA5_MIN_VIDEO_SECONDS} seconds or longer — long-form or Shorts both work).`,
      /**
       * ★**각주** — 시안 `30708:140107` (2026-09-11 추가분).
       *
       * *"TactSuit 없어도 응모 가능"*. 이게 없으면 장비가 없는 사람이 **자격이 없다고
       * 읽고 이탈한다** — 캠페인이 신규 유입을 노리는데 정반대로 걸러내는 문구가 된다.
       *
       * `chips` 와 같은 자리(2번 카드의 칩과 y 가 같다)이고, **데이터로** 둔다 —
       * 「1번 카드에만」을 인덱스로 박으면 순서가 바뀔 때 엉뚱한 카드에 붙는다.
       */
      note: "*No TactSuit required to enter.",
    },
    {
      title: "Upload & tag",
      desc: `Upload it to YouTube with the hashtag ${GTA5_HASHTAG} and tag ${GTA5_MENTION}.`,
      chips: [GTA5_HASHTAG, GTA5_MENTION],
    },
    {
      title: "Submit",
      desc: "Submit your video link below — uploading to YouTube alone does not complete your entry.",
    },
  ],

  /**
   * ★**`Feel GTA V, Don't Just Play It`** — 시안 `30550:7291` (2026-09-10 실측).
   *
   * 카드 3장. 시안의 카드에는 **미디어가 없다** — 디자이너가 빈 채로 뒀고 영상은 따로
   * 지정했다(구간 3개, 크기 미정). 그래서 지금은 글자만 있는 그라디언트 카드다.
   * 상세는 `Gta5FeelCards` docblock.
   */
  feelTitle: "Feel GTA V, Don't Just Play It",
  feelDesc:
    "Every explosion, every chase, every hit — felt on your skin, not just seen on screen.",
  /**
   * ★**영상이 들어왔다** (2026-09-11 · 작성자 제공 · CDN 업로드 완료).
   *
   * 종전엔 시안의 미디어 자리가 **비어 있어** 카드가 그라디언트만이었다. 보드가
   * 갱신돼 셋 다 게임플레이 클립으로 채워졌다(`30550:7296` · `30769:156341`).
   *
   * ```
   *   원본      1920×1080 · 60fps · 6s · 8.9~18.4Mbps (합계 29.4MB)
   *   인코딩    720p60 · H.264 CRF 26 · 오디오 없음 · +faststart
   *   결과      2.12 / 3.12 / 1.29 MB  (합계 6.54MB)
   *   실측      2026-09-11 curl — 셋 다 HTTP/2 200 · video/mp4
   *             content-length 가 로컬 인코딩 결과와 바이트 단위로 일치
   * ```
   *
   * ⚠️ `poster` 는 **각 영상의 첫 프레임**이다(ffmpeg → sharp webp q80, 708×398).
   * 없으면 `ViewportVideo` 기본 스켈레톤(`bg-white/50`)이 이 검정 카드에서
   * **흰 판으로 번쩍인다** — 느린 회선에서 오래 보인다.
   */
  feelCards: [
    {
      title: "Explosions",
      desc: "Grenade or rocket — each blast hits your chest differently.",
      video: "https://cdn.bhaptics.com/media/gta5/feel-explosions.mp4",
      poster: "/images/event/gta5-feel-explosions.webp",
    },
    {
      title: "Speed",
      desc: "Feel the road, the impact, the wind — on your skin.",
      video: "https://cdn.bhaptics.com/media/gta5/feel-speed.mp4",
      poster: "/images/event/gta5-feel-speed.webp",
    },
    {
      title: "Action",
      desc: "Punch or bullet — each blow hits your gut differently.",
      video: "https://cdn.bhaptics.com/media/gta5/feel-action.mp4",
      poster: "/images/event/gta5-feel-action.webp",
    },
  ],

  /** ★`Step Into the Game` 머리말 — 시안 `30550:234170` (2026-09-10 실측). */
  stepIntoEyebrow: "A new way to game",
  stepIntoTitle: "Step Into the Game",
  stepIntoDesc:
    "TactSuit turns in-game moments into physical sensations on your body — so you feel every moment.",

  /**
   * ★**응모 머리말** — 시안 `30541:6847` (2026-09-10 실측).
   *
   * 종전은 `ENTER NOW` / `Submit Your Entry` / *"We only use this to pick winners
   * and ship your prize. Nothing else."* 였다. 시안은 셋 다 다르고, 설명이
   * **위저드 구조를 안내**한다(왼쪽 가이드 · 오른쪽 입력).
   *
   * ⚠️ **버린 문장이 개인정보 사용 고지였다** — 이름·전화·주소를 받는 폼 위에서
   * 그걸 그냥 지우면 고지가 사라진다. 확인해 보니 **이미 더 나은 자리에 있다**:
   * `WizPaneDelivery`(주소를 실제로 받는 단계)가 *"Used only for winner selection
   * & delivery"* · *"Deleted 30 days after the event ends"* 를 불릿으로 든다.
   * 수집 지점에 붙어 있는 쪽이 맞으므로 머리말은 시안대로 간다.
   */
  /**
   * ★**마감 후 화면** — 페이지는 살아 있지만 접수는 닫혔다(리뷰 #5723 R1 M2).
   *
   * 폼을 그대로 두면 제출할 때마다 서버가 403 을 준다 — 사용자는 자기가 뭘 잘못했나
   * 찾게 된다. 접수 상태를 **서버가 판정해 프롭으로** 내리고, 닫혔으면 폼 자리에
   * 이 문구를 그린다.
   */
  /**
   * ★발표 후 문구 — 핸드오프 `30606:7324` ②: *"now ≥ WINNERS → 카운트다운 제거,
   * **`Winners announced` 문구** 또는 결과 링크"*.
   *
   * 결과 링크는 **넣지 않는다** — 발표 채널이 이메일 + 공식 SNS 라 리포에 목적지가
   * 없다. 없는 링크를 만들면 죽은 앵커가 된다.
   */
  winnersAnnounced: "Winners announced",

  /**
   * ★**타임라인 상태 문구** — 시안 `30784:7927` (`Desktop — Timeline`, 2026-09-11).
   *
   * 그 보드는 카운트다운 머리글을 **상태마다** 갈아 끼운다. 종전 구현은 `judging`
   * 에 `WINNERS IN` 을 쓰고 타깃을 발표일로 바꿔 **카운트다운을 이어 돌렸는데**,
   * 시안은 숫자를 `00` 으로 죽이고 머리글을 「접수가 끝났다」로 바꾼다.
   *
   * ```
   *   open       ENTRIES CLOSE IN         ← 마감까지 라이브
   *   judging    ENTRIES ARE NOW CLOSED   ← **00** 고정 · opacity 50
   *   announced  WINNERS ANNOUNCED        ← 숫자 자리에 winnersStatusLine
   * ```
   *
   * ⚠️ 의미가 다르다. 「발표까지 N일」은 **다음 일정**을 재촉하고, 「접수 끝」은
   * **이번 일정의 종료**를 알린다. 시안이 후자를 골랐다.
   */
  cdHeadOpen: "ENTRIES CLOSE IN",
  cdHeadClosed: "ENTRIES ARE NOW CLOSED",

  /**
   * 발표 후 숫자 자리에 들어가는 한 줄 — 시안 `30788:157477` (`status-line`).
   * 14/18 Regular `#999EA6` tracking .25.
   *
   * 위 `winnersAnnounced` 의 docblock 이 *"결과 링크는 넣지 않는다"* 를 적어 뒀고
   * 이 문장이 그 자리를 대신한다 — 목적지 대신 **어디를 보라**고만 말한다.
   */
  winnersStatusLine: "Check your email & bHaptics socials.",

  /** 페이즈 칩 — 활성 페이즈에만 붙는다(시안 `30792:234682` 계열). */
  chipLive: "LIVE",
  chipClosed: "CLOSED",
  chipAnnounced: "ANNOUNCED",

  entriesClosedTitle: "Entries are closed",
  entriesClosedDesc: `Submissions closed on ${GTA5_CLOSE_LABEL_LONG_TZ}. Winners are picked by valid view count and announced ${GTA5_ANNOUNCE_LABEL_LONG} — by email and on bHaptics' official social media channels.`,

  /**
   * ★**URL 검증 4상태** — 시안 `30647:6903`(idle) · `30663:6926`(valid) ·
   * `30663:7023`(notFound) · `30663:7120`(required) · 2026-09-11 실측.
   *
   * 응모자가 링크를 붙여 넣으면 그 자리에 영상이 뜬다. 문구가 상태마다 다르고 **색도
   * 다르다**(회색 → 초록 → 빨강). 화면이 상태를 말해 주지 않으면 사용자는 왜 다음으로
   * 못 가는지 모른다.
   *
   * ⚠️ `checking` 은 **시안에 없다.** 조회가 왕복이라 그 사이에 화면이 필요하고,
   * 없으면 idle 문구가 남아 「아무 일도 안 일어난다」로 보인다. 우리가 정한 자리다.
   */
  videoHelpIdle:
    "Paste the Video link you copied from YouTube — the preview below confirms it's the right video.",
  videoHelpChecking: "Checking your link…",
  videoHelpValid:
    "Great — we found your video. Confirm the details below and continue.",
  videoHelpNotFound:
    "We couldn't find that video. Check the link is public and correct, then try again.",
  videoHelpRequired: "Please paste your YouTube video link to continue.",
  /**
   * 조회 **자체가** 실패했을 때. `notFound` 와 갈라 쓴다 — 이건 사용자 잘못이 아니라
   * 우리 쪽 문제라 **다음 단계를 막지 않는다**(작성자 확정 2026-09-11).
   */
  videoHelpError:
    "We couldn't check the link right now. You can continue — we'll verify it later.",

  videoPreviewIdleTitle: "Your video preview will appear here",
  videoPreviewIdleDesc: "once you paste a valid link (16:9)",
  videoPreviewNotFoundTitle: "Video not found",
  videoPreviewNotFoundDesc: "check the link and paste again",

  /** 확인 체크박스를 안 켜고 Next 를 누른 경우 — 시안 `30664:234186`. */
  errAttestRequired: "You must confirm this to enter.",

  entryEyebrow: "Giveaway entry",
  entryTitle: "Enter in 3 Steps",
  entryDesc:
    "Each step shows you exactly what to do — guide on the left, your input on the right.",
  fieldVideoLink: "YouTube video link",
  fieldFullName: "Full name",
  fieldPhone: "Phone number (please include country code)",
  fieldEmail: "Email address",
  fieldAddress: "Shipping address, including country",
  fieldPostalCode: "Postal / ZIP code",
  ctaSending: "Submitting…",
  /** 시안 `30575:7677` 그대로 — 종전엔 `"Required"` 한 단어였다. */
  errRequired: "This field is required.",
  /**
   * 시안 `30576:7668` 그대로.
   *
   * ★종전 문구는 *"please use the form below instead"* 였는데 **아래에 폼이 없다** —
   * 리포 전역에 대체 폼 링크가 0건이다(`grep "form below"` = 이 한 줄뿐). 실패한
   * 사용자를 존재하지 않는 것으로 안내했다.
   */
  errSubmitFailed:
    "Something went wrong on our end. Please check your connection and try again. If it keeps happening, contact support@bhaptics.com.",

  /**
   * ★**마감은 「우리 쪽 문제」가 아니다.**
   *
   * 라우트가 창 밖이면 **403 `closed`** 를 준다(`canAcceptEntry`). 그런데 화면은
   * 모든 실패를 한 문구로 뭉쳐 *"우리 쪽 문제니 다시 시도하세요"* 라고 했다 —
   * 마감 뒤에 페이지를 열어 둔 사용자는 **아무리 눌러도 403** 이라 영원히 재시도한다.
   *
   * 그래서 이 사유만 갈라 쓴다. 시안(`30576:7664`)엔 배너가 한 벌뿐이라 **모양은
   * 그대로 쓰고 문구만** 바꾼다 — 나머지 사유(네트워크·502·400)는 시안 문구 그대로다.
   */
  errSubmitClosedTitle: "Entries are closed",
  errSubmitClosed: `Entries closed on ${GTA5_CLOSE_LABEL_TZ}. Winners are announced ${GTA5_ANNOUNCE_LABEL} on bHaptics official channels.`,
  /**
   * 시안 `30581:7500` 그대로.
   *
   * ★종전 문구는 *"We'll email you if you're one of them"* 이었는데 **같은 카드의
   * 각주가 정반대**를 말했다 — *"A confirmation isn't sent by email"*. 한 화면에서
   * 메일을 보낸다/안 보낸다가 동시에 떴다.
   *
   * 2026-09-21 부터 **확인 메일을 보낸다**(`GTA5_CONFIRMATION_SUBJECT`). 각주는 이제
   * 라우트의 `emailed` 에 따라 「{{email}} 로 보냈다」/「이번엔 못 보냈다」 두 벌이다
   * (`event.json` 의 `successFootnote` · `successFootnoteNoMail`) — 안 간 메일을 「보냈다」고
   * 띄우지 않는다.
   */
  entrySubmittedDesc: `Your GTA V clip is entered — good luck! The top ${GTA5_PRIZE_COUNT} videos by view count each win a TactSuit Pro.`,

  prizeEyebrow: "THE PRIZE",
  prizeTitle: "Own Your TactSuit",
  prizeLines: [
    `${GTA5_PRIZE_COUNT} TactSuit Pro, shipped worldwide — on us.`,
    `Enter by ${GTA5_CLOSE_LABEL_TZ}. Top ${GTA5_PRIZE_COUNT} by view count, not random draw.`,
    `Winners get an email, announced ${GTA5_ANNOUNCE_LABEL} on bHaptics socials.`,
  ],

  /**
   * ★**상품 카드** — 시안 `30551:7418` (2026-09-10 `get_design_context` 실측).
   *
   * ⚠️ **`get_metadata` 의 레이어 **이름**은 낡아 있었다** — 한글 셋(`고밀도 진동
   * 모터` …)에 `✓` 가 글자로 박혀 있어 체크 아이콘과 두 벌이었다. `get_design_context`
   * 로 뜬 **현재 텍스트**는 영문이고 `✓` 도 없다. 이름이 아니라 텍스트를 쓴다 —
   * 전역 규칙 「피그마 하자 = 전수 재조사」가 정확히 이 자리를 잡았다.
   */
  prizeCardEyebrow: "Ultimate Immersion",
  prizeCardTitle: "TactSuit Pro",
  prizeCardFeatures: [
    "Stronger, more precise haptic feedback",
    "32 vibration points for more detailed feedback",
    "Recommended for FPS, action, and racing games",
  ],
  prizeCardCta: "Learn More",

  rulesTitle: "Official Rules",
  /**
   * 시안 `2337:1241` 원문 그대로다. **문장을 줄이지 말 것** — 18세 제한 · 조회수 기준 ·
   * 30일 삭제 · 배송비/관세 부담 · EU/UK 열람·정정·삭제권이 전부 법적 약속이다.
   */
  rules: [
    "No purchase necessary to enter. Void where prohibited or restricted by law. You must be 18 years of age or older to enter. Winners are selected based on valid view count during the entry period; bHaptics reserves the right to disqualify fraudulent entries and to modify, suspend, or cancel this promotion if necessary. By submitting an entry, you agree to these Official Rules.",
    "Your information will be used solely for winner selection and prize fulfillment, and deleted 30 days after the event concludes. Shipping and any customs duties will be covered by bHaptics.",
    "If you are located in the EU/EEA, UK, or another region with data protection laws, you have the right to request access to, correction of, or deletion of your data at any time by contacting us at support@bhaptics.com or bHaptics Inc., 201-B, 361-17 Gapcheon-ro, Yuseong-gu, Daejeon 34037, Republic of Korea.",
  ],
} as const;
